import { useState, type ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Input } from '@/src/elements/input'
import { Message } from '@/src/elements/message'
import { SOCIAL_NAME } from '@/src/features/auth/models/auth.enums'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { ICON } from '@/src/general/enums/icons'
import { parseApiErrors } from '@/src/general/utilities/fetcher/parseApiError'
import { useTranslate, type TMessageKey } from '@/src/lang/useTranslate'

import { useSettingsMutation } from '../../services/useSettingsMutation'
import { SettingsProfileInput } from './SettingsProfileInput/SettingsProfileInput'

import type { TMessageId } from '@/translations/translations.types'

type TPasswordFormValues = { oldPassword: TMessageId | ''; password: TMessageId | ''; confirmPassword: TMessageId | '' }
const initialValues: TPasswordFormValues = { oldPassword: '', password: '', confirmPassword: '' }
const initialsShowValues = { oldPassword: false, password: false, confirmPassword: false }
const fields: { name: keyof TPasswordFormValues; labelKey: TMessageKey }[] = [
  { name: 'oldPassword', labelKey: 'settings.account.current-password' },
  { name: 'password', labelKey: 'settings.account.new-password' },
  { name: 'confirmPassword', labelKey: 'settings.account.confirm-password' },
]

export const PasswordForm = (): ReactNode => {
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState<Partial<TPasswordFormValues> & { global?: TMessageId }>({})
  const [showValues, setShowValues] = useState<Record<keyof TPasswordFormValues, boolean>>(initialsShowValues)

  const { t } = useTranslate()
  const { data: me } = useMeQuery()

  const { mutateAsync } = useSettingsMutation()

  const handleUncaughtSubmit = async () => {
    const newErrors: Partial<TPasswordFormValues> = {}
    if (!me?.socialConnect && !values.oldPassword) {
      newErrors.oldPassword = 'common.form.error.field.required'
    }
    if (!values.password) {
      newErrors.password = 'common.form.error.field.required'
    }
    if (values.password === me?.name) {
      newErrors.password = 'common.form.error.password-is-name'
    }
    if (values.confirmPassword !== values.password) {
      newErrors.confirmPassword = 'common.form.error.password.different'
    }
    if (Object.values(newErrors).length > 0) {
      setErrors(newErrors)
      throw new Error('common.form.error.field.invalid')
    }
    try {
      await mutateAsync({ password: values.password, oldPassword: values.oldPassword })
      setValues(initialValues)
    } catch (error) {
      const apiErrorsList = parseApiErrors(error, Object.keys(initialValues))
      const apiErrors = apiErrorsList?.reduce<Partial<TPasswordFormValues>>((acc, [fieldName, errorKey]) => {
        acc[fieldName] = errorKey
        return acc
      }, {})
      if (apiErrors && Object.values(apiErrors).length > 0) {
        console.log('apiErrors', apiErrors)
        setErrors(apiErrors)
      } else {
        setErrors({ global: 'common.form.error.fail' })
      }
      throw error
    }
  }

  return (
    <SettingsProfileInput
      onCancel={() => setShowValues(initialsShowValues)}
      label={t('settings.input.password.label')}
      title=""
      classNames={{ header: 'pb-0' }}
      value="******"
      onUncaughtSubmit={handleUncaughtSubmit}
    >
      {({ onSubmitEditing }) => (
        <View className="gap-4 pb-4">
          {fields.map((field) => {
            if (me?.socialConnect && field.name === 'oldPassword') {
              return (
                <Message key="socialMessage" colors="success">
                  {t('settings.input.password.social-connect', {
                    socialName: me.socialAuthProviders?.[0] || SOCIAL_NAME.GOOGLE, // Arbitrary fallback - should not happen
                  })}
                </Message>
              )
            }

            const fieldError = errors[field.name]

            return (
              <Input
                inBottomSheet
                key={field.name}
                secureTextEntry={!showValues[field.name]}
                label={t(field.labelKey)}
                value={values[field.name]}
                onChangeText={(newValue) => {
                  setErrors((prevErrors) => ({ ...prevErrors, [field.name]: '' }))
                  setValues((prevValues) => ({ ...prevValues, [field.name]: newValue }))
                }}
                placeholder="******"
                onSubmitEditing={onSubmitEditing}
                errorMessage={fieldError ? t(fieldError) : undefined}
                endContent={
                  <Button
                    size="xs"
                    isIconOnly
                    variant="light"
                    contrast="low"
                    colors="default"
                    onPress={() =>
                      setShowValues((prevShowValues) => ({
                        ...prevShowValues,
                        [field.name]: !prevShowValues[field.name],
                      }))
                    }
                    startIcon={ICON.EYE}
                  />
                }
              />
            )
          })}
          {errors.global && <Message colors="danger">{errors.global}</Message>}
        </View>
      )}
    </SettingsProfileInput>
  )
}
